<script setup lang="ts">
import { Ref, computed, ref } from 'vue';
import { throttle as _throttle, clamp } from 'lodash';
import { FullColorObject, HsvColor } from '@/composables/color';

const props = defineProps<{
    value: FullColorObject;
}>();

const emit = defineEmits<{
    (event: 'change', value: Partial<FullColorObject>): void;
}>();

const containerRef: Ref<HTMLDivElement | null> = ref(null);

const bgColor = computed(() => `hsl(${props.value.hsv.h}, 100%, 50%)`);
const pointerTop = computed(() => -props.value.hsv.v + 1 + 100 + '%');
const pointerLeft = computed(() => props.value.hsv.s + '%');

const handleChange = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();

    const container = containerRef.value;

    // for some edge cases, container may not exist
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const xOffset = container.getBoundingClientRect().left + window.scrollX;
    const yOffset = container.getBoundingClientRect().top + window.scrollY;
    const pageX = (e as MouseEvent).pageX || ((e as TouchEvent).touches ? (e as TouchEvent).touches[0].pageX : 0);
    const pageY = (e as MouseEvent).pageY || ((e as TouchEvent).touches ? (e as TouchEvent).touches[0].pageY : 0);
    const left = clamp(pageX - xOffset, 0, containerWidth);
    const top = clamp(pageY - yOffset, 0, containerHeight);
    const saturation = left / containerWidth;
    const bright = clamp(-(top / containerHeight) + 1, 0, 1);

    throttle({
        h: props.value.hsv.h,
        s: saturation * 100,
        v: bright * 100
    });
};

const onChange = (hsv: HsvColor) => {
    emit('change', { hsv });
};

const throttle = _throttle(onChange, 20, {
    leading: true,
    trailing: false
});

const handleMouseDown = (event: PointerEvent) => {
    event.preventDefault();
    const element = containerRef.value;
    if (!element) return;
    element.setPointerCapture(event.pointerId);
    handleChange((event as unknown) as MouseEvent);
};

const handleMouseMove = (event: PointerEvent) => {
    const element = containerRef.value;
    if (!element?.hasPointerCapture?.(event.pointerId)) return;
    handleChange((event as unknown) as MouseEvent);
};

const handleMouseUp = (event: PointerEvent) => {
    const element = containerRef.value;
    if (!element?.hasPointerCapture?.(event.pointerId)) return;
    handleChange((event as unknown) as MouseEvent);
    element.releasePointerCapture(event.pointerId);
};
</script>

<template>
    <div
        ref="containerRef"
        class="c-saturation"
        :style="{ background: bgColor }"
        @pointerdown="handleMouseDown"
        @pointermove="handleMouseMove"
        @pointerup="handleMouseUp"
        @pointercancel="handleMouseUp"
    >
        <div class="c-saturation--white" />
        <div class="c-saturation--black" />
        <div class="c-saturation-pointer" :style="{ top: pointerTop, left: pointerLeft }">
            <div class="c-saturation-circle" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
%position {
    cursor: pointer;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0.25rem;
}

.c-saturation {
    @extend %position;
    .c-saturation--white {
        @extend %position;
        background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0));
    }
    .c-saturation--black {
        @extend %position;
        background: linear-gradient(to top, #000, rgba(0, 0, 0, 0));
    }
    .c-saturation-pointer {
        cursor: pointer;
        position: absolute;

        .c-saturation-circle {
            cursor: pointer;
            width: 0.5rem;
            height: 0.5rem;
            box-shadow: 0 0 0 1.5px #fff, inset 0 0 1px 1px rgba(0, 0, 0, 0.3), 0 0 1px 2px rgba(0, 0, 0, 0.4);
            border-radius: 50%;
            transform: translate(-2px, -2px);
        }
    }
}
</style>
